import React, { Fragment, useState } from 'react';
import { FormGroup, Label, Input } from 'reactstrap';
import { isFunction, get, isArray, } from 'lodash';
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';
import classnames from 'classnames';
import DatePicker from 'react-datepicker';

import DateSelector from './DateSelector';
import NumberInput from './NumberInput';
import texts from '../shared/texts';

const validationText = (documentName, fieldName, key) => {
  return (
    get(texts.validations[documentName], `${fieldName}.${key}`)
    || get(texts.validations.general, key)
  );
}

export default function Field (props) {
  const { documentName, type, name: fieldName, label, value, setValue, options: _options = [], validationErrors = [], readOnly = _ => false, shouldHide, placeholder, selector = false, yearRange = [], values, hint, inputProps = {}, suffixContent = _ => null, rows, } = props;
  const options = isFunction(_options) ? _options(values) : _options;
  const [hasStarted, setHasStarted] = useState(false);
  const setValueAndStart = (value) => {
    setValue(value);
    setHasStarted(true);
  };
  const isValid = validationErrors.length === 0;
  const validationCss = hasStarted && classnames({ 'is-valid': isValid, 'is-invalid': isValid === false });
  if(shouldHide) return null;

  return (
    <FormGroup key={fieldName}>
      {
        ({
          string: () => (
            <Fragment>
              {label && <Label>{label}</Label>}
              <Input name={fieldName} value={value || ''} onChange={_ => setValueAndStart(_.target.value)} className={validationCss} readOnly={readOnly(value, props)} placeholder={placeholder} />
            </Fragment>
          ),
          password: () => (
            <Fragment>
              {label && <Label>{label}</Label>}
              <Input type="password" name={fieldName} value={value || ''} onChange={_ => setValueAndStart(_.target.value)} className={validationCss} readOnly={readOnly(value, props)} placeholder={placeholder} />
            </Fragment>
          ),
          integer: () => (
            <Fragment>
              {label && <Label>{label}</Label>}
              <NumberInput name={fieldName} value={value != null ? value : ''} onChange={_ => setValueAndStart(_)} className={classnames('text-right', validationCss)} readOnly={readOnly(value, props)} placeholder={placeholder} />
            </Fragment>
          ),
          float: () => (
            <Fragment>
              {label && <Label>{label}</Label>}
              <NumberInput name={fieldName} value={value != null ? value : ''} onChange={_ => setValueAndStart(_)} className={classnames('text-right', validationCss)} readOnly={readOnly(value, props)} placeholder={placeholder} />
            </Fragment>
          ),
          boolean: () => (
            <Fragment>
              <FormGroup check>
                <Label check>
                  <Input name={fieldName} type="checkbox" checked={value} onChange={_ => setValueAndStart(_.target.checked)} className={validationCss} disabled={readOnly(value, props)} />
                  {label}
                </Label>
              </FormGroup>
            </Fragment>
          ),
          select: () => (
            <Fragment>
              {label && <Label>{label}</Label>}
              <Select
                data-test={fieldName}
                value={options.find(_ => _.value === value) || null}
                onChange={_ => setValueAndStart(_ && _.value)}
                className={classnames('form-select', validationCss)}
                options={options}
                isDisabled={readOnly(value, props)}
                isClearable
                {...inputProps}
              />
            </Fragment>
          ),
          multiSelect: () => (
            <Fragment>
              {label && <Label>{label}</Label>}
              <Select
                data-test={fieldName}
                isMulti
                value={(isArray(value) ? value : []).map(v => options.find(_ => _.value === v)) || null}
                onChange={_ => setValueAndStart((_ || []).map(_ => _.value))}
                className={classnames('form-select', validationCss)}
                options={options}
                isDisabled={readOnly(value, props)}
                isClearable
                {...inputProps}
              />
            </Fragment>
          ),
          creatableMultiSelect: () => (
            <Fragment>
              {label && <Label>{label}</Label>}
              <CreatableSelect
                data-test={fieldName}
                isMulti
                value={(value || []).map(_ => ({ label: _, value: _ })) || null}
                onChange={_ => setValueAndStart((_ || []).map(_ => _.value))}
                className={classnames('form-select', validationCss)}
                options={options}
                isDisabled={readOnly(value, props)}
                isClearable
              />
            </Fragment>
          ),
          datetime: () => (
            <Fragment>
              {label && <Label>{label}</Label>}
              <div>
                <DatePicker
                  data-test={fieldName}
                  showTimeSelect
                  dateFormat="yyyy/MM/dd HH:mm"
                  timeFormat="HH:mm"
                  timeIntervals={10}
                  selected={value}
                  onChange={setValueAndStart}
                  className={classnames('form-control', validationCss)}
                  readOnly={readOnly(value, props)}
                  placeholder={placeholder}
                />
              </div>
            </Fragment>
          ),
          date: () => (
            <Fragment>
              {label && <Label>{label}</Label>}
              <div>
                {
                  selector ? (
                    <DateSelector
                      data-test={fieldName}
                      value={value}
                      onChange={setValueAndStart}
                      className={classnames(validationCss)}
                      readOnly={readOnly(value, props)}
                      yearRange={yearRange}
                      {...inputProps}
                    />
                  ) : (
                    <DatePicker
                      data-test={fieldName}
                      dateFormat="yyyy/MM/dd"
                      timeIntervals={10}
                      selected={value}
                      onChange={setValueAndStart}
                      className={classnames('form-control', validationCss)}
                      readOnly={readOnly(value, props)}
                      placeholder={placeholder}
                    />
                  )
                }
              </div>
            </Fragment>
          ),
          time: () => (
            <Fragment>
              {label && <Label>{label}</Label>}
              <div>
                <DatePicker
                  data-test={fieldName}
                  showTimeSelect
                  showTimeSelectOnly
                  dateFormat="HH:mm"
                  timeFormat="HH:mm"
                  timeIntervals={5}
                  selected={value}
                  onChange={setValueAndStart}
                  className={classnames('form-control', validationCss)}
                  readOnly={readOnly(value, props)}
                  placeholder={placeholder}
                />
              </div>
            </Fragment>
          ),
          text: () => (
            <Fragment>
              {label && <Label>{label}</Label>}
              <Input type="textarea" value={value} onChange={_ => setValueAndStart(_.target.value)} className={validationCss} readOnly={readOnly(value, props)} placeholder={placeholder} rows={rows} />
            </Fragment>
          ),
        })[type]()
      }
      {hint && <small className="form-text text-muted" style={{ whiteSpace: 'pre-line' }}>{hint}</small>}
      {
        hasStarted && type !== 'list' && validationErrors.length > 0 && (
          <div className="small text-danger mt-1">
            {
              validationErrors.map(k => (
                <div key={k}>{validationText(documentName, fieldName.replace(/\.\d+\./g, '.fields.'), k)}</div>
              ))
            }
          </div>
        )
      }
      {suffixContent()}
    </FormGroup>
  );
};
